import type { Hero } from '../types';

/** 数式は docs/SMART_DRAFT_WIN_RATE_PREDICTION.md（実装監査節参照） */

/**
 * 2体のヒーロー間のカウンター効果を計算（味方視点）
 */
export function calculateCounterBonus(allyHero: Hero, enemyHero: Hero): number {
  const fromAlly = allyHero.matchups.find(
    m => m.opponent_hero_id === enemyHero.id && m.match_type === 0
  );

  if (fromAlly) {
    return fromAlly.increase_win_rate;
  }

  const fromEnemy = enemyHero.matchups.find(
    m => m.opponent_hero_id === allyHero.id && m.match_type === 0
  );

  if (fromEnemy) {
    return -fromEnemy.increase_win_rate;
  }

  return 0;
}

function calculateTotalCounterBonus(allyHeroes: Hero[], enemyHeroes: Hero[]): number {
  let totalBonus = 0;
  for (const ally of allyHeroes) {
    for (const enemy of enemyHeroes) {
      totalBonus += calculateCounterBonus(ally, enemy);
    }
  }
  return totalBonus;
}

/**
 * 2体のヒーロー間のシナジー効果を計算
 */
export function calculateSynergyBonus(heroA: Hero, heroB: Hero): number {
  const fromA = heroA.matchups.find(
    m => m.opponent_hero_id === heroB.id && m.match_type === 1
  );
  if (fromA) {
    return fromA.increase_win_rate;
  }

  const fromB = heroB.matchups.find(
    m => m.opponent_hero_id === heroA.id && m.match_type === 1
  );
  if (fromB) {
    return fromB.increase_win_rate;
  }

  return 0;
}

function calculateTeamSynergy(heroes: Hero[]): number {
  let totalSynergy = 0;
  for (let i = 0; i < heroes.length; i++) {
    for (let j = i + 1; j < heroes.length; j++) {
      totalSynergy += calculateSynergyBonus(heroes[i], heroes[j]);
    }
  }
  return totalSynergy;
}

/**
 * 基本勝率 + カウンター効果 + シナジー効果を計算（味方推定勝率）
 */
export function calculateWinRate(allyHeroes: Hero[], enemyHeroes: Hero[]): number {
  const allyContribution = allyHeroes.reduce((sum, h) => sum + (h.win_rate - 0.5), 0);
  const enemyContribution = enemyHeroes.reduce((sum, h) => sum + (h.win_rate - 0.5), 0);
  const counterBonus = calculateTotalCounterBonus(allyHeroes, enemyHeroes);
  const allySynergy = calculateTeamSynergy(allyHeroes);
  const enemySynergy = calculateTeamSynergy(enemyHeroes);

  const winRate =
    0.5 + allyContribution - enemyContribution + counterBonus + allySynergy - enemySynergy;

  return Math.max(0, Math.min(1, winRate));
}
